import {
  Box,
  Button,
  Flex,
  Input,
  Select as NativeSelect,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useToast,
} from "@chakra-ui/react";
import { LocalDate } from "@js-joda/core";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import React from "react";
import { ResponseOf } from "../../core/api";
import { STATES } from "../../shared/components/AddressPicker/address-picker-consts";
import SingleDatePicker from "../../shared/components/DatePicker/SingleDatePicker";
import Page from "../../shared/components/Page";
import Select from "../../shared/components/Select";
import useApi from "../../shared/hooks/useApi";
import { AgencyMemberId } from "../../shared/schema/schema";
import { dateFormatter } from "../../shared/utils/date-formatter";
import { formatErrorResponse } from "../../shared/utils/format-response-error";

type License = ResponseOf<"get", "./agency_member_licenses">["licenses"][number];

const LICENSE_TYPES = ["RN", "LPN", "NP", "BSN"] as const;

// The address picker's list starts with New York; here it reads alphabetically.
const STATE_OPTIONS = STATES.map((state) => ({
  label: `${state.label} (${state.value})`,
  value: state.value,
})).sort((a, b) => a.label.localeCompare(b.label));

type Draft = {
  agencyMemberId: AgencyMemberId | null;
  state: string | null;
  licenseType: typeof LICENSE_TYPES[number];
  licenseNumber: string;
  /** Optional: a license with no expiry counts as in force until it is removed. */
  expiresOn: LocalDate | null;
};

const EMPTY_DRAFT: Draft = {
  agencyMemberId: null,
  state: null,
  licenseType: "RN",
  licenseNumber: "",
  expiresOn: null,
};

/**
 * The agency's nurses' professional licenses.
 *
 * What decides who may sign daily notes: every signature needs a license in force, whose type and
 * number print on the note's PDF, and an office can require it to be in its own state (Georgia,
 * for visits from July 1, 2026). The expiry is optional; a license without one stays in force
 * until it is removed. A license is removed rather than edited, so a signed note keeps the one it
 * was signed under.
 */
export default function DailyNoteLicensesTab() {
  const { queries, mutations } = useApi();
  const queryClient = useQueryClient();
  const toast = useToast();

  const [draft, setDraft] = React.useState<Draft>(EMPTY_DRAFT);

  const licenses = useQuery(queries.dailyNotes.licenses());
  const members = useQuery(queries.agencyMember.list());

  const memberOptions = React.useMemo(
    () =>
      (members.data?.agencyMembers ?? [])
        .map((member) => ({
          label: `${member.firstName} ${member.lastName}`,
          value: member.id,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [members.data]
  );

  const onError = (title: string) => (error: unknown) =>
    toast({
      title,
      description: formatErrorResponse(error),
      status: "error",
      position: "top-right",
      isClosable: true,
      duration: 9000,
    });

  const create = useMutation({
    ...mutations.dailyNotes.createLicense(),
    onSuccess: () => {
      setDraft(EMPTY_DRAFT);
      // The sign-off check reads licenses too, so an open attestation re-asks.
      void queryClient.invalidateQueries({ queryKey: ["agencyMemberLicenses"] });
      void queryClient.invalidateQueries({ queryKey: ["visitDailyNotes", "signOffCheck"] });
      toast({ title: "License added", status: "success", position: "top-right" });
    },
    onError: onError("The license wasn't added"),
  });

  const remove = useMutation({
    ...mutations.dailyNotes.removeLicense(),
    onSuccess: (response) => {
      queryClient.setQueryData(queries.dailyNotes.licenses().queryKey, response);
      void queryClient.invalidateQueries({ queryKey: ["visitDailyNotes", "signOffCheck"] });
    },
    onError: onError("The license wasn't removed"),
  });

  const canAdd =
    draft.agencyMemberId !== null && draft.state !== null && draft.licenseNumber.trim() !== "";

  const onAdd = () => {
    if (draft.agencyMemberId === null || draft.state === null) {
      return;
    }

    create.mutate({
      agencyMemberId: draft.agencyMemberId,
      state: draft.state,
      licenseType: draft.licenseType,
      licenseNumber: draft.licenseNumber.trim(),
      expiresOn: draft.expiresOn,
    });
  };

  return (
    <Box>
      <Page.Header pb={2}>
        <Page.Title>Licenses</Page.Title>
      </Page.Header>

      <Page.Content pt={1}>
        <Text color="gray.600" fontSize="md" mb={4}>
          Only nurses with a license in force can sign daily notes, and the license number prints on
          each signed note. Some offices also require the license to be in their state.
        </Text>

        <Flex align="flex-end" gap={3} mb={6} wrap="wrap">
          <Select
            allowUnselect={true}
            isLoading={members.isPending}
            label="Member"
            multiple={false}
            options={memberOptions}
            searchable={true}
            value={draft.agencyMemberId}
            onChange={(value) => setDraft((prev) => ({ ...prev, agencyMemberId: value ?? null }))}
          />
          <Select
            allowUnselect={true}
            label="State"
            multiple={false}
            options={STATE_OPTIONS}
            searchable={true}
            value={draft.state}
            onChange={(value) => setDraft((prev) => ({ ...prev, state: value ?? null }))}
          />
          <NativeSelect
            value={draft.licenseType}
            width="100px"
            onChange={(event) =>
              setDraft((prev) => ({
                ...prev,
                licenseType: event.target.value as Draft["licenseType"],
              }))
            }
          >
            {LICENSE_TYPES.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </NativeSelect>
          <Input
            placeholder="License number"
            value={draft.licenseNumber}
            width="200px"
            onChange={(event) =>
              setDraft((prev) => ({ ...prev, licenseNumber: event.target.value }))
            }
          />
          <SingleDatePicker
            placeholderText="Expires on (optional)"
            selected={draft.expiresOn}
            onChange={(date) => setDraft((prev) => ({ ...prev, expiresOn: date }))}
          />
          <Button
            colorScheme="blue"
            isDisabled={!canAdd}
            isLoading={create.isPending}
            onClick={onAdd}
          >
            Add license
          </Button>
        </Flex>

        {licenses.isPending ? (
          <Spinner size="sm" />
        ) : (licenses.data ?? []).length === 0 ? (
          <Text color="gray.500">No licenses have been added yet.</Text>
        ) : (
          <Table size="md" variant="simple">
            <Thead>
              <Tr>
                <Th>Member</Th>
                <Th>State</Th>
                <Th>Type</Th>
                <Th>Number</Th>
                <Th>Expires</Th>
                <Th />
              </Tr>
            </Thead>
            <Tbody>
              {(licenses.data ?? []).map((license: License) => {
                const expired =
                  license.expiresOn !== null && license.expiresOn.isBefore(LocalDate.now());

                return (
                  <Tr key={String(license.id)}>
                    <Td>{license.agencyMemberName}</Td>
                    <Td>{license.state}</Td>
                    <Td>{license.licenseType}</Td>
                    <Td>{license.licenseNumber}</Td>
                    <Td>
                      {license.expiresOn === null ? (
                        <Text color="gray.500">No expiry</Text>
                      ) : (
                        <Text color={expired ? "red.700" : undefined}>
                          {dateFormatter.toDate(license.expiresOn)}
                          {expired ? " (expired)" : ""}
                        </Text>
                      )}
                    </Td>
                    <Td textAlign="right">
                      <Button
                        isLoading={remove.isPending && remove.variables === license.id}
                        size="sm"
                        variant="outline"
                        onClick={() => remove.mutate(license.id)}
                      >
                        Remove
                      </Button>
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        )}
      </Page.Content>
    </Box>
  );
}
